/* Independent PictoMini story products. No legacy-product lookup or fallback. */
(function () {
  'use strict';
  const MAX_BYTES = 8 * 1024 * 1024;
  const text = value => String(value == null ? '' : value);
  function validConfig(config) {
    return Boolean(config && config.version === 1 && config.productId && config.handle && config.example_key &&
      config.subject_family && Number.isInteger(config.humans) && config.humans >= 0 &&
      Number.isInteger(config.pets) && config.pets >= 0 && config.humans + config.pets > 0 &&
      config.photo_limit === 6 && ['approved', 'quote_required'].includes(config.pricing_state) &&
      Array.isArray(config.variants) && config.variants.length &&
      config.variants.every(v => v.id && text(v.productId) === text(config.productId) && v.size && v.finish));
  }
  function purchasable(config, variant) {
    return validConfig(config) && config.purchase_enabled === true && config.pricing_state === 'approved' &&
      Boolean(variant && config.variants.some(v => text(v.id) === text(variant.id)) &&
      text(variant.productId) === text(config.productId) && variant.available === true && Number(variant.price) > 0);
  }
  function pickedVariant(config, id) {
    return config.variants.find(v => text(v.id) === text(id)) || null;
  }
  function resolveNativeVariant(config, selectedId, href) {
    if (!validConfig(config)) return null;
    const url = new URL(href);
    const ids = url.searchParams.getAll('variant');
    if (/\/products_preview\/?$/.test(url.pathname) && ids.length !== 1) return null;
    if (ids.length > 1 || (ids.length === 1 && !/^\d+$/.test(ids[0]))) return null;
    if (ids.length) return pickedVariant(config, ids[0]);
    return pickedVariant(config, selectedId) || config.variants[0];
  }
  async function validateFile(file, decode) {
    if (!file || !file.size) throw new Error('file_invalid');
    if (file.size > MAX_BYTES) throw new Error('file_large');
    const extension = /\.([a-z0-9]+)$/i.exec(file.name || '')?.[1].toLowerCase();
    const expected = { jpg: 'jpeg', jpeg: 'jpeg', png: 'png', webp: 'webp' }[extension];
    if (!expected || (file.type && file.type !== 'image/' + expected)) throw new Error('file_invalid');
    const bytes = new Uint8Array(await file.slice(0, 12).arrayBuffer());
    const kind = [137,80,78,71,13,10,26,10].every((n, i) => bytes[i] === n) ? 'png' :
      bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255 ? 'jpeg' :
      String.fromCharCode(...bytes.slice(0,4)) === 'RIFF' && String.fromCharCode(...bytes.slice(8,12)) === 'WEBP' ? 'webp' : '';
    if (kind !== expected) throw new Error('file_invalid');
    try { await decode(file); } catch (_) { throw new Error('file_decode'); }
    return file;
  }
  function expectedProperties(config, variant, notes, id) {
    const properties = {
      _pictomini_design_id: id,
      _pictomini_product_handle: config.handle,
      _pictomini_product_kind: 'story',
      _pictomini_example_key: config.example_key,
      _pictomini_subject_family: config.subject_family,
      _pictomini_humans: text(config.humans),
      _pictomini_pets: text(config.pets),
      _pictomini_subjects: text(config.humans + config.pets),
      _pictomini_size: variant.size,
      _pictomini_finish: variant.finish,
      _pictomini_variant_title: variant.title,
      _pictomini_sku: variant.sku || ''
    };
    if (notes.trim()) properties['Customization notes'] = notes.trim().slice(0,1500);
    return properties;
  }
  function makeAttempt(config, variant, photos, notes, id) {
    if (!validConfig(config)) throw new Error('config_unavailable');
    if (!photos.length) throw new Error('photo_required');
    if (photos.length > config.photo_limit) throw new Error('too_many');
    if (!purchasable(config, variant)) throw new Error('not_available');
    return { id, product: text(config.productId), variant: text(variant.id), photos: photos.length,
      properties: expectedProperties(config, variant, notes, id) };
  }
  function buildForm(attempt, photos) {
    const form = new FormData();
    form.set('id', attempt.variant); form.set('quantity', '1');
    Object.entries(attempt.properties).forEach(([key, value]) => form.set('properties[' + key + ']', value));
    photos.forEach((photo, i) => form.set('properties[Reference photo ' + (i + 1) + ']', photo.file, photo.file.name));
    return form;
  }
  function validLine(line, attempt) {
    if (!line || text(line.product_id) !== attempt.product || text(line.variant_id) !== attempt.variant ||
      line.quantity !== 1 || !line.properties) return false;
    if (!Object.entries(attempt.properties).every(([key, value]) => text(line.properties[key]) === text(value))) return false;
    return Array.from({length: attempt.photos}, (_, i) => line.properties['Reference photo ' + (i + 1)]).every(value => {
      try { const url = new URL(value); return url.protocol === 'https:' && url.hostname === 'cdn.shopify.com' && url.pathname.includes('/uploads/'); }
      catch (_) { return false; }
    });
  }
  class CartAttempt {
    constructor(config, transport, persist = () => {}, pending = null) {
      this.config = config; this.transport = transport; this.persist = persist; this.pending = pending;
      this.busy = false; this.confirmed = false;
    }
    async recover() {
      if (!this.pending) throw new Error('uncertain');
      const cart = await this.transport.read();
      const matches = cart.items.filter(line => line.properties?._pictomini_design_id === this.pending.id);
      if (matches.length !== 1 || !validLine(matches[0], this.pending)) throw new Error('uncertain');
      this.pending = null; this.persist(null); this.confirmed = true; return cart;
    }
    async submit(variant, photos, notes, id) {
      if (this.busy) throw new Error('uploading');
      if (this.confirmed) throw new Error('added');
      this.busy = true;
      try {
        if (this.pending) return await this.recover(); // Never repeat an unconfirmed POST.
        const attempt = makeAttempt(this.config, variant, photos, notes, id);
        this.pending = attempt; this.persist(attempt);
        try {
          const result = await this.transport.send(buildForm(attempt, photos));
          if (!result.ok) {
            if ([400,404,422].includes(result.status)) {
              this.pending = null; this.persist(null); throw new Error('not_available');
            }
            throw new Error('uncertain');
          }
        } catch (error) {
          if (!this.pending) throw error;
          // Timeout/parse/5xx can occur after Shopify accepted the upload.
        }
        return await this.recover();
      } finally { this.busy = false; }
    }
  }
  const exportsForTests = { MAX_BYTES, validConfig, purchasable, pickedVariant, resolveNativeVariant, validateFile,
    makeAttempt, buildForm, validLine, expectedProperties, CartAttempt };
  if (typeof module !== 'undefined' && module.exports) { module.exports = exportsForTests; return; }

  function loadImage(image) {
    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = error => { if (settled) return; settled = true; clearTimeout(timer); image.removeEventListener('load', onLoad); image.removeEventListener('error', onError); error ? reject(error) : resolve(); };
      const onLoad = () => image.naturalWidth ? finish() : finish(new Error('image'));
      const onError = () => finish(new Error('image'));
      const timer = setTimeout(onError, 12000);
      image.addEventListener('load', onLoad); image.addEventListener('error', onError);
      image.loading = 'eager';
      if (image.complete) onLoad();
      else if (image.decode) image.decode().then(onLoad).catch(() => { if (image.complete) onLoad(); });
    });
  }
  class StoryBuilder extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController(); this.alive = true; this.imageRequest = 0;
      this.photos = []; this.reading = false; this.imageLoading = false; this.step = 0;
      this.find = selector => this.querySelector(selector);
      this.all = selector => Array.from(this.querySelectorAll(selector));
      try {
        this.config = JSON.parse(this.find('[data-story-config]').textContent);
        this.strings = JSON.parse(this.find('[data-story-strings]').textContent);
      } catch (_) { return; }
      this.current = resolveNativeVariant(this.config, this.dataset.selectedVariant, location.href);
      this.identityError = !this.current || text(this.dataset.productId) !== text(this.config.productId) || this.dataset.productHandle !== this.config.handle;
      this.maxStep = 0;
      this.sessionKey = 'pm-story-attempt-v1:' + this.config.productId;
      const persist = attempt => { try { attempt ? sessionStorage.setItem(this.sessionKey, JSON.stringify(attempt)) : sessionStorage.removeItem(this.sessionKey); } catch (_) {} };
      let pending = null;
      try { const item = JSON.parse(sessionStorage.getItem(this.sessionKey)); if (item?.product === text(this.config.productId) && item.id && item.properties && Number.isInteger(item.photos)) pending = item; } catch (_) {}
      const root = this.config.rootUrl.endsWith('/') ? this.config.rootUrl : this.config.rootUrl + '/';
      const request = async (url, init, timeout) => {
        const controller = new AbortController(); const timer = setTimeout(() => controller.abort(), timeout);
        try { return await fetch(url, { credentials:'same-origin', ...init, signal:controller.signal }); }
        finally { clearTimeout(timer); }
      };
      this.cart = new CartAttempt(this.config, {
        send: form => request(root + 'cart/add.js', {method:'POST', body:form, headers:{Accept:'application/json'}},120000),
        read: async () => { const response = await request(root + 'cart.js', {cache:'no-store',headers:{Accept:'application/json'}},15000); const cart = await response.json(); if (!response.ok || !Array.isArray(cart.items)) throw new Error('uncertain'); return cart; }
      }, persist, pending);
      const on = (node, event, callback) => node?.addEventListener(event, callback, {signal:this.controller.signal});
      on(this.find('[data-story-files]'), 'change', event => this.acceptFiles(event.target.files));
      const drop = this.find('[data-story-drop]');
      on(drop, 'dragover', event => { event.preventDefault(); if (!this.locked()) drop.dataset.drag = 'true'; });
      on(drop, 'dragleave', () => delete drop.dataset.drag);
      on(drop, 'drop', event => { event.preventDefault(); delete drop.dataset.drag; if (!this.locked()) this.acceptFiles(event.dataTransfer.files); });
      this.all('[data-story-variant]').forEach(input => on(input,'change', () => this.selectVariant(input.value)));
      this.all('[data-story-thumb]').forEach(button => on(button,'click', () => this.showMedia(button.dataset.storyThumb)));
      on(this.find('[data-story-next]'), 'click', () => this.next());
      on(this.find('[data-story-back]'), 'click', () => this.go(this.step - 1));
      this.all('[data-story-step-link]').forEach(button => on(button, 'click', () => this.go(Number(button.dataset.storyStepLink))));
      this.all('[data-story-edit]').forEach(button => on(button, 'click', () => this.go(Number(button.dataset.storyEdit))));
      on(this.find('[data-story-recover]'), 'click', () => this.checkout());
      on(window, 'popstate', () => {
        const variant = resolveNativeVariant(this.config, this.dataset.selectedVariant, location.href);
        if (!variant) { this.identityError = true; this.controls(); return; }
        if (this.identityError) return;
        this.selectVariant(variant.id, false);
      });
      if (!this.identityError) {
        this.dataset.selectedVariant = text(this.current.id);
        this.all('[data-story-variant]').forEach(input => input.checked = text(input.value) === text(this.current.id));
      }
      if (pending) this.error('uncertain');
      this.controls();
      if (!this.identityError) this.showMedia(this.current.mediaId);
    }
    disconnectedCallback() {
      this.alive = false; this.imageRequest++; this.controller?.abort(); this.controller = null;
      this.photos?.forEach(photo => URL.revokeObjectURL(photo.url)); delete this.dataset.storyReady;
    }
    say(key) { return this.strings[key] || key; }
    error(key) { const box = this.find('[data-story-error]'); box.textContent = key ? this.say(key) : ''; box.hidden = !key; }
    status(key) { const box = this.find('[data-story-status]'); box.textContent = key ? this.say(key) : ''; box.hidden = !key; }
    locked() { return this.identityError || this.reading || this.imageLoading || this.cart.busy || Boolean(this.cart.pending) || this.cart.confirmed; }
    price() { return purchasable(this.config,this.current) ? this.current.formattedPrice : this.say('price_pending'); }
    controls() {
      this.find('[data-story-identity]').hidden = !this.identityError;
      this.find('[data-story-content]').hidden = this.identityError;
      this.dataset.storyReady = this.identityError ? 'blocked' : 'true';
      const locked = this.locked();
      this.all('[data-story-variant],[data-story-files],[data-story-notes],[data-story-remove]').forEach(node => node.disabled = locked);
      const photoHint = this.find('[data-story-photo-hint]');
      if (photoHint) photoHint.hidden = this.step !== 0 || this.photos.length > 0;
      const button = this.find('[data-story-next]');
      button.disabled = !validConfig(this.config) || locked || !this.photos.length || (this.step === 2 && !purchasable(this.config,this.current));
      this.find('[data-story-next-label]').textContent = this.cart.confirmed ? this.say('added') : this.step === 0 ? this.say('photo_next') : this.step === 1 ? this.say('details_next') : purchasable(this.config,this.current) ? this.say('add_cart') : this.say('not_available');
      this.find('[data-story-back]').disabled = locked;
      this.find('[data-story-back]').hidden = this.step === 0;
      this.all('[data-story-step-link]').forEach(button => {
        const step = Number(button.dataset.storyStepLink);
        button.disabled = locked || (step > 0 && !this.photos.length) || step > this.maxStep;
        if (step === this.step) button.setAttribute('aria-current', 'step'); else button.removeAttribute('aria-current');
      });
      this.all('[data-story-edit]').forEach(button => button.disabled = locked);
      this.find('[data-story-recover]').hidden = !this.cart.pending;
      this.find('[data-story-recover]').disabled = this.cart.busy;
      this.find('[data-story-cart]').hidden = !this.cart.pending && !this.cart.confirmed;
      this.find('[data-story-price]').textContent = this.price();
      this.find('[data-story-action-price]').textContent = this.price();
      this.find('[data-story-preview-note]').hidden = purchasable(this.config,this.current);
      this.setAttribute('aria-busy', this.reading || this.imageLoading || this.cart.busy ? 'true' : 'false');
    }
    async showMedia(id) {
      const panels = this.all('[data-story-media]'); const target = panels.find(node => node.dataset.storyMedia === text(id));
      if (!target) { this.error('image_unavailable'); return; }
      const request = ++this.imageRequest; this.imageLoading = true; this.controls(); this.status('loading');
      try {
        const image = target.querySelector('img'); if (image) await loadImage(image);
        if (!this.alive || request !== this.imageRequest) return;
        panels.forEach(panel => { if (panel !== target) panel.querySelectorAll('video').forEach(video => video.pause()); panel.hidden = panel !== target; });
        this.all('[data-story-thumb]').forEach(button => button.setAttribute('aria-pressed', button.dataset.storyThumb === text(id) ? 'true' : 'false'));
        if (image) this.all('[data-story-figure]').forEach(holder => {
          const thumbnail = image.cloneNode(); thumbnail.removeAttribute('fetchpriority'); thumbnail.loading = 'lazy'; thumbnail.alt = this.say('your_figure'); holder.replaceChildren(thumbnail);
        });
        this.status('');
      } catch (_) { if (this.alive && request === this.imageRequest) { this.error('image_unavailable'); this.status(''); } }
      finally { if (this.alive && request === this.imageRequest) { this.imageLoading = false; this.controls(); } }
    }
    selectVariant(id, updateUrl = true) {
      if (this.identityError || this.reading || this.cart.busy || this.cart.pending || this.cart.confirmed) return;
      const variant = pickedVariant(this.config,id); if (!variant) { this.identityError = true; this.controls(); return; }
      this.dataset.selectedVariant = text(variant.id);
      this.current = variant;
      this.all('[data-story-variant]').forEach(input => input.checked = text(input.value) === text(id));
      if (updateUrl) { const url = new URL(location.href); url.searchParams.set('variant',text(variant.id)); history.replaceState({},'',url); }
      this.error(''); this.controls(); this.showMedia(variant.mediaId);
    }
    async acceptFiles(files) {
      if (this.locked()) return;
      this.reading = true; this.error(''); this.controls(); const errors = [];
      for (const file of Array.from(files)) {
        if (this.photos.length >= this.config.photo_limit) { errors.push('too_many'); break; }
        if (this.photos.some(photo => photo.file.name === file.name && photo.file.size === file.size && photo.file.lastModified === file.lastModified)) { errors.push('file_duplicate'); continue; }
        let url;
        try {
          await validateFile(file, async input => { url = URL.createObjectURL(input); const img = new Image(); img.src = url; await loadImage(img); });
          if (!this.alive) { if (url) URL.revokeObjectURL(url); break; }
          this.photos.push({file,url});
        } catch (error) { if (url) URL.revokeObjectURL(url); errors.push(error.message); }
      }
      if (!this.alive) return;
      this.reading = false; this.find('[data-story-files]').value = ''; this.renderPhotos();
      this.error(errors[0] || ''); this.controls();
    }
    renderPhotos() {
      const list = this.find('[data-story-photos]'); list.replaceChildren();
      this.photos.forEach((photo,index) => {
        const item = document.createElement('li'); const image = document.createElement('img');
        image.src = photo.url; image.alt = this.say('photos') + ' ' + (index+1); image.width = 96; image.height = 96;
        const button = document.createElement('button'); button.type='button'; button.dataset.storyRemove='';
        button.textContent = this.say('remove_photo'); button.setAttribute('aria-label', this.say('remove_photo') + ' ' + (index+1));
        button.addEventListener('click', () => { if(this.locked()) return; URL.revokeObjectURL(photo.url); this.photos.splice(index,1); this.renderPhotos(); if (!this.photos.length) { this.maxStep = 0; this.go(0); } this.controls(); }, {signal:this.controller.signal});
        item.append(image,button); list.append(item);
      });
    }
    go(step) {
      if (this.locked()) return;
      if (!Number.isInteger(step) || step < 0 || step > 2) return;
      if (step > 0 && !this.photos.length) { this.error('photo_required'); this.find('[data-story-files]').focus(); return; }
      if (step > this.maxStep + 1) return;
      this.error(''); this.step = step;
      this.maxStep = Math.max(this.maxStep, step);
      this.find('[data-story-editor]').hidden = step === 2;
      this.all('[data-story-screen]').forEach(screen => screen.hidden = Number(screen.dataset.storyScreen) !== step);
      if (step === 2) {
        this.find('[data-story-review-size]').textContent = this.current.displaySize || this.current.size;
        this.find('[data-story-review-price]').textContent = this.price();
        this.find('[data-story-review-count]').textContent = text(this.photos.length);
        this.find('[data-story-review-notes]').textContent = this.find('[data-story-notes]').value.trim() || this.say('no_notes');
        const list = this.find('[data-story-review-photos]'); list.replaceChildren();
        this.photos.forEach(photo => { const image = document.createElement('img'); image.src=photo.url; image.alt=this.say('photos'); image.width=96; image.height=96; list.append(image); });
        this.find('[data-story-review]').focus({preventScroll:true});
      }
      this.controls();
      const heading = this.find('[data-story-screen="' + step + '"] [data-story-step-heading]');
      heading?.focus({preventScroll:true});
      this.find('[data-story-stepper]').scrollIntoView({block:'start',behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
    }
    next() { if (this.identityError) return; if (this.step < 2) this.go(this.step + 1); else this.checkout(); }
    async checkout() {
      if (this.identityError || this.cart.busy || this.reading || this.imageLoading) return;
      if (!this.cart.pending && (this.step !== 2 || !this.photos.length)) { this.error('photo_required'); return; }
      if (!this.cart.pending && !purchasable(this.config,this.current)) { this.error('not_available'); return; }
      this.status('uploading'); this.error('');
      try {
        const promise = this.cart.submit(this.current,this.photos,this.find('[data-story-notes]').value,crypto.randomUUID());
        this.controls(); const cart = await promise;
        this.status('added');
        try { if (window.PictoMiniCart) await window.PictoMiniCart.open(this.find('[data-story-next]'),null,cart); } catch (_) {}
      } catch (error) { this.status(''); this.error(this.cart.pending ? 'uncertain' : error.message); }
      finally { if (this.alive) this.controls(); }
    }
  }
  if (!customElements.get('pm-story-builder')) customElements.define('pm-story-builder',StoryBuilder);
})();


